import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import {
  useCreateUserGeneratedContentMutation,
  useUpdateUserGeneratedContentMutation,
  useContentSubmissionMutation,
  useUserGeneratedContentQuery,
} from '../../common/queries/use-user-generated-content-query';
import {
  MediaGroup,
  UserGeneratedContent,
  UserGeneratedContentResponse,
} from './types';
import { fromApiPayload } from '../../services/user-generated-content';
import { upsertAttachmentGroup } from '../../screens/user-generated-contents/form/media/attachment-group';
import { usePatronSelector } from '../../common/use-patron-selector';
import { programSelectors } from '../program';
import { advocateSelectors } from '../advocate';

export const MAXIMUM_TITLE_LENGTH = 125;
export const MAXIMUM_SUMMARY_LENGTH = 250;

type UpsertUserGeneratedContentType = {
  onSuccess: (response?: UserGeneratedContentResponse) => void;
  onError: (error: Error) => void;
  publicationState?: 'draft';
};
type AttachmentGroupItems<T extends MediaGroup['type']> = Extract<
  MediaGroup,
  { type: T }
>['items'];

type UpdateAttachmentGroup = <T extends MediaGroup['type']>(
  type: T,
  updater: (
    prev: AttachmentGroupItems<T>
  ) => AttachmentGroupItems<T> | undefined
) => void;

export type MemberValidationField =
  | 'title'
  | 'topic'
  | 'body'
  | 'audience'
  | 'notification'
  | 'notificationDetails'
  | 'limitPostNeedsTargets';

type UserGeneratedContentContextType = {
  userGeneratedContent: UserGeneratedContent;
  updateUserGeneratedContentFormState: (
    data: Partial<UserGeneratedContent>
  ) => void;
  updateAttachmentGroup: UpdateAttachmentGroup;

  upsertUserGeneratedContent: (props: UpsertUserGeneratedContentType) => void;
  submitUserGeneratedContent: (props: UpsertUserGeneratedContentType) => void;
  archiveUserGeneratedContent: (props: UpsertUserGeneratedContentType) => void;

  isPersisting: boolean;
  hasChanges: boolean;
  isTitleOverLimit: boolean;
  error: string | undefined;
  isLoading: boolean;
  isNewRecord: boolean;
  isEditLocked: boolean;

  memberValidationErrors: Record<MemberValidationField, boolean>;
  validateForSave: () => boolean;
  validateForSubmit: () => boolean;
};

const defaultUserGeneratedContent: UserGeneratedContent = {
  title: '',
  body: '',
  topic_ids: [],
  audiences: [],
  is_translatable: false,
  is_commentable: false,
  is_shareable: false,
  limit_visibility_to_audience: false,
};

const UserGeneratedContentContext = createContext<
  UserGeneratedContentContextType | undefined
>(undefined);

type UserGeneratedContentProviderProps = {
  children: ReactNode;
  id?: number;
};

export function UserGeneratedContentProvider({
  children,
  id,
}: UserGeneratedContentProviderProps) {
  const isNewRecord = !id;
  const [isReady, setIsReady] = useState(isNewRecord);
  const commentingDefaultEnabled = usePatronSelector(
    programSelectors.getSubmissionsCommentDefaultEnabled
  );
  const translationAvailable = usePatronSelector(
    advocateSelectors.getCanTranslateSubmission
  );
  // useState initializer runs once at mount. program state is always populated
  // in Redux before this provider renders (gated by PatronData's isInitialized).
  const [userGeneratedContent, setUserGeneratedContent] =
    useState<UserGeneratedContent>(() => ({
      ...defaultUserGeneratedContent,
      is_commentable: isNewRecord ? !!commentingDefaultEnabled : false,
      // When translation is available for this user/program, default it on
      // for new submissions (the option is hidden in the dropdown otherwise).
      is_translatable: isNewRecord ? !!translationAvailable : false,
    }));
  const [hasChanges, setHasChanges] = useState(false);
  const updateUserGeneratedContentFormState = useCallback(
    (updates: Partial<UserGeneratedContent>) => {
      setUserGeneratedContent((prev) => ({ ...prev, ...updates }));
      setHasChanges(true);
    },
    []
  );

  // Race-safe upsert for a single media group within attachments. Both image
  // and video uploads can complete concurrently; reading attachments via refs
  // and writing a derived array allowed the later writer to overwrite the
  // earlier one. Computing inside the functional setter avoids that.
  const updateAttachmentGroup = useCallback<UpdateAttachmentGroup>(
    (type, updater) => {
      setUserGeneratedContent((prev) => {
        const current = prev.attachments ?? [];
        const group = current.find((g) => g.type === type);
        // Generic T isn't preserved through useCallback's inference, so we
        // bridge to the union type via `any` here. The public signature on
        // `UpdateAttachmentGroup` keeps callers type-safe.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newItems = (updater as (prev: any) => any)(group?.items ?? []);
        return {
          ...prev,
          attachments: upsertAttachmentGroup(current, type, newItems),
        };
      });
      setHasChanges(true);
    },
    []
  );

  const { data: fetchedRecord, error: fetchError } =
    useUserGeneratedContentQuery(id);

  useEffect(() => {
    if (fetchedRecord && !isNewRecord) {
      setUserGeneratedContent((prev) => ({
        ...prev,
        ...fromApiPayload(fetchedRecord.data.attributes),
        id: fetchedRecord.data.attributes.id,
        title: fetchedRecord.data.attributes.title,
        summary: fetchedRecord.data.attributes.summary,
        body: fetchedRecord.data.attributes.body,
      }));
      setIsReady(true);
    } else if (fetchError && !isNewRecord) {
      setIsReady(true);
    }
  }, [fetchedRecord, fetchError, isNewRecord]);

  const onSaveSuccess = useCallback(
    (response: UserGeneratedContentResponse) => {
      updateUserGeneratedContentFormState({
        ...fromApiPayload(response.data.attributes),
        id: response.data.attributes.id,
        title: response.data.attributes.title,
        summary: response.data.attributes.summary,
        body: response.data.attributes.body,
        publication_state: response.data.attributes.publication_state,
        content_cover: response.data.attributes.content_cover,
      });
    },
    [updateUserGeneratedContentFormState]
  );

  const onSaveError = useCallback((err) => {
    console.error('User generated content submission error', err);
  }, []);

  const { isWorking: isCreating, mutate: createMutation } =
    useCreateUserGeneratedContentMutation({
      onSuccess: onSaveSuccess,
      onError: onSaveError,
    });

  const { isWorking: isUpdating, mutate: updateMutateFn } =
    useUpdateUserGeneratedContentMutation(userGeneratedContent.id, {
      onSuccess: onSaveSuccess,
      onError: onSaveError,
    });

  const updateMutation = useCallback(
    (
      data: Partial<UserGeneratedContent>,
      options: {
        onSuccess: (response?: UserGeneratedContentResponse) => void;
        onError: (error: Error) => void;
      }
    ) => {
      if (data.id) {
        updateMutateFn(data, options);
      }
    },
    [updateMutateFn]
  );

  const upsertUserGeneratedContent = useCallback(
    ({
      onSuccess,
      onError,
      publicationState,
    }: UpsertUserGeneratedContentType) => {
      // Strip publication_state from form state — it is only included when
      // explicitly provided (e.g. 'draft' on Save as Draft). The publish path
      // omits it entirely so mojo applies its default of 'published'. See FE-30694.
      const { publication_state: _omitted, ...contentWithoutState } =
        userGeneratedContent;
      const base = publicationState
        ? { ...contentWithoutState, publication_state: publicationState }
        : contentWithoutState;
      // The mention picker appends a trailing space after a token for editing
      // ergonomics (see reference-text-input); trim trailing whitespace from
      // title/summary so it is never persisted. (FE-34788)
      const payload = {
        ...base,
        ...(typeof base.title === 'string'
          ? { title: base.title.trimEnd() }
          : {}),
        ...(typeof base.summary === 'string'
          ? { summary: base.summary.trimEnd() }
          : {}),
      };

      if (payload.id) {
        updateMutation(payload, { onSuccess, onError });
      } else {
        createMutation(payload, { onSuccess, onError });
      }
    },
    [createMutation, updateMutation, userGeneratedContent]
  );

  const { isWorking: isSubmitting, mutate: statusMutation } =
    useContentSubmissionMutation();

  const submittingRef = useRef(false);

  const submitUserGeneratedContent = useCallback(
    ({ onSuccess, onError }: UpsertUserGeneratedContentType) => {
      if (submittingRef.current) return;
      submittingRef.current = true;

      upsertUserGeneratedContent({
        onSuccess: (response?: UserGeneratedContentResponse) => {
          // New records are created with no publication_state, so mojo defaults
          // them to 'published' directly. Calling /submit on already-published
          // content is redundant and could cause an unintended state transition,
          // so we skip it for new records. See FE-30694.
          if (isNewRecord) {
            submittingRef.current = false;
            onSuccess(response);
            return;
          }

          const contentId =
            response?.data?.attributes?.id ?? userGeneratedContent.id;
          if (!contentId) {
            submittingRef.current = false;
            onError(new Error('Content ID not available after save'));
            return;
          }
          statusMutation(
            { id: contentId },
            {
              onSuccess: () => {
                submittingRef.current = false;
                onSuccess();
              },
              onError: (err) => {
                submittingRef.current = false;
                onError(err);
              },
            }
          );
        },
        onError: (err) => {
          submittingRef.current = false;
          onError(err);
        },
      });
    },
    [
      isNewRecord,
      upsertUserGeneratedContent,
      statusMutation,
      userGeneratedContent.id,
    ]
  );

  const archiveUserGeneratedContent = useCallback(
    ({ onSuccess, onError }: UpsertUserGeneratedContentType) => {
      if (!userGeneratedContent.id) {
        return;
      }
      updateMutateFn({ publication_state: 'archived' }, { onSuccess, onError });
    },
    [updateMutateFn, userGeneratedContent.id]
  );

  const isTitleOverLimit = useMemo(() => {
    if (!userGeneratedContent.title) {
      return false;
    }
    return userGeneratedContent.title.length > MAXIMUM_TITLE_LENGTH;
  }, [userGeneratedContent.title]);

  const isSummaryOverLimit = useMemo(() => {
    if (!userGeneratedContent.summary) {
      return false;
    }
    return userGeneratedContent.summary.length > MAXIMUM_SUMMARY_LENGTH;
  }, [userGeneratedContent.summary]);

  // "Limit post visibility" focuses a post on specific audiences within a
  // topic, so it only makes sense with both a topic AND an audience selected.
  // Mobile (Studio) enforces this; web matches it so the same content is
  // submittable across platforms.
  const hasFocusedContentMissingTargets = useMemo(
    () =>
      !!userGeneratedContent.limit_visibility_to_audience &&
      ((userGeneratedContent.audiences?.length ?? 0) === 0 ||
        (userGeneratedContent.topic_ids?.length ?? 0) === 0),
    [
      userGeneratedContent.limit_visibility_to_audience,
      userGeneratedContent.audiences,
      userGeneratedContent.topic_ids,
    ]
  );

  // Inline per-field validation errors. Set explicitly on Save/Publish, and
  // auto-cleared in real time as the user fixes each field.
  const [memberValidationErrors, setMemberValidationErrors] = useState<
    Record<MemberValidationField, boolean>
  >({
    title: false,
    topic: false,
    body: false,
    audience: false,
    notification: false,
    notificationDetails: false,
    limitPostNeedsTargets: false,
  });

  const hasValidTitle = useMemo(
    () => !!userGeneratedContent.title?.trim(),
    [userGeneratedContent.title]
  );

  const hasTopicOrAudience = useMemo(() => {
    const hasTopic = !!userGeneratedContent.topic_ids?.length;
    const hasAudience = !!userGeneratedContent.audiences?.length;
    return hasTopic || hasAudience;
  }, [userGeneratedContent.topic_ids, userGeneratedContent.audiences]);

  const hasNotificationChannel = useMemo(() => {
    const ns = userGeneratedContent.notification_settings;
    return !!(
      ns?.notification_center?.enabled ||
      ns?.email?.enabled ||
      ns?.push_notification?.enabled
    );
  }, [userGeneratedContent.notification_settings]);

  const hasAudienceWithoutChannel = useMemo(() => {
    const hasAudience = !!userGeneratedContent.audiences?.length;
    return hasAudience && !hasNotificationChannel;
  }, [userGeneratedContent.audiences, hasNotificationChannel]);

  // An enabled channel whose required text is blank (Notification Center and
  // Push need a message, Email a subject). The notification modal's Apply guards
  // this, but a channel can reach form state already enabled-but-empty without
  // going through Apply — via program channel defaults (which seed toggles on)
  // or a reloaded draft. Publish must re-check, otherwise the empty payload is
  // sent and pony's notification validators reject it with a generic error.
  // Gated on audience: notifications only apply when an audience is selected, so
  // seeded-but-orphaned channels left over after removing the audience must not
  // block publish or leave a stuck error the audience-gated editor can't clear.
  const hasEnabledChannelMissingContent = useMemo(() => {
    if (!userGeneratedContent.audiences?.length) return false;
    const ns = userGeneratedContent.notification_settings;
    if (!ns) return false;
    const ncMissing =
      !!ns.notification_center?.enabled &&
      !ns.notification_center?.message?.trim();
    const emailMissing = !!ns.email?.enabled && !ns.email?.subject?.trim();
    const pushMissing =
      !!ns.push_notification?.enabled && !ns.push_notification?.message?.trim();
    return ncMissing || emailMissing || pushMissing;
  }, [
    userGeneratedContent.audiences,
    userGeneratedContent.notification_settings,
  ]);

  // Computing this requires parsing rich-text HTML with DOMParser, so we only
  // run it on demand (validate-on-click + auto-clear when the body error is
  // already showing) rather than re-parsing on every keystroke via useMemo.
  const computeHasBodyOrAttachment = useCallback(() => {
    const rawBody = userGeneratedContent.body ?? '';
    let bodyText = rawBody;
    if (rawBody && typeof DOMParser !== 'undefined') {
      const doc = new DOMParser().parseFromString(rawBody, 'text/html');
      bodyText = doc.body.textContent ?? '';
    }
    const hasBody = bodyText.trim().length > 0;
    const hasAttachment = !!userGeneratedContent.attachments?.some(
      (g) => (g.items?.length ?? 0) > 0
    );
    return hasBody || hasAttachment;
  }, [userGeneratedContent.body, userGeneratedContent.attachments]);

  // Auto-clear: as soon as the underlying field becomes valid, drop its error.
  // The topic and audience errors share the same "Topic and/or audience
  // required" rule — picking either clears both.
  useEffect(() => {
    setMemberValidationErrors((prev) =>
      prev.title && hasValidTitle ? { ...prev, title: false } : prev
    );
  }, [hasValidTitle]);
  useEffect(() => {
    setMemberValidationErrors((prev) => {
      if (!hasTopicOrAudience) return prev;
      if (!prev.topic && !prev.audience) return prev;
      return { ...prev, topic: false, audience: false };
    });
  }, [hasTopicOrAudience]);
  useEffect(() => {
    setMemberValidationErrors((prev) =>
      prev.notification && !hasAudienceWithoutChannel
        ? { ...prev, notification: false }
        : prev
    );
  }, [hasAudienceWithoutChannel]);
  useEffect(() => {
    setMemberValidationErrors((prev) =>
      prev.notificationDetails && !hasEnabledChannelMissingContent
        ? { ...prev, notificationDetails: false }
        : prev
    );
  }, [hasEnabledChannelMissingContent]);
  // Clears when the user supplies the missing topic/audience or unchecks
  // Limit post.
  useEffect(() => {
    setMemberValidationErrors((prev) =>
      prev.limitPostNeedsTargets && !hasFocusedContentMissingTargets
        ? { ...prev, limitPostNeedsTargets: false }
        : prev
    );
  }, [hasFocusedContentMissingTargets]);
  // Only run the expensive HTML parse when a body error is currently showing
  // and the body/attachments have changed since.
  useEffect(() => {
    setMemberValidationErrors((prev) => {
      if (!prev.body) return prev;
      return computeHasBodyOrAttachment() ? { ...prev, body: false } : prev;
    });
  }, [computeHasBodyOrAttachment]);

  const validateForSave = useCallback(() => {
    const titleError = !hasValidTitle;
    setMemberValidationErrors((prev) => ({ ...prev, title: titleError }));
    return !titleError;
  }, [hasValidTitle]);

  const validateForSubmit = useCallback(() => {
    const titleError = !hasValidTitle;
    // "Topic and/or audience required" — when neither is selected, flag both
    // pickers so users get the inline error next to each. Adding either one
    // clears both.
    const topicOrAudienceMissing = !hasTopicOrAudience;
    const bodyError = !computeHasBodyOrAttachment();
    const notificationError = hasAudienceWithoutChannel;
    const notificationDetailsError = hasEnabledChannelMissingContent;
    const limitPostError = hasFocusedContentMissingTargets;
    setMemberValidationErrors({
      title: titleError,
      topic: topicOrAudienceMissing,
      body: bodyError,
      audience: topicOrAudienceMissing,
      notification: notificationError,
      notificationDetails: notificationDetailsError,
      limitPostNeedsTargets: limitPostError,
    });
    // Over-limit title/summary block the click without an inline flag — the
    // TextInput already shows a red character counter, and the maxLength prop
    // makes this only reachable for records loaded with over-limit values.
    return (
      !titleError &&
      !topicOrAudienceMissing &&
      !bodyError &&
      !notificationError &&
      !notificationDetailsError &&
      !limitPostError &&
      !isTitleOverLimit &&
      !isSummaryOverLimit
    );
  }, [
    hasValidTitle,
    hasTopicOrAudience,
    hasAudienceWithoutChannel,
    hasEnabledChannelMissingContent,
    hasFocusedContentMissingTargets,
    isTitleOverLimit,
    isSummaryOverLimit,
    computeHasBodyOrAttachment,
  ]);

  // Trust a loaded publication_state over fetchError so a transient refetch
  // failure (window-focus refetch, reconnect) doesn't yank an active draft
  // editor to the locked page.
  const isEditLocked =
    !isNewRecord &&
    isReady &&
    (userGeneratedContent.publication_state
      ? userGeneratedContent.publication_state !== 'draft'
      : !!fetchError);

  const value = {
    userGeneratedContent,
    isNewRecord,
    updateUserGeneratedContentFormState,
    updateAttachmentGroup,
    isPersisting: isCreating || isUpdating || isSubmitting,
    hasChanges,
    isTitleOverLimit,
    error: fetchError?.message,
    upsertUserGeneratedContent,
    submitUserGeneratedContent,
    archiveUserGeneratedContent,
    isLoading: !isReady,
    isEditLocked,
    memberValidationErrors,
    validateForSave,
    validateForSubmit,
  };

  return (
    <UserGeneratedContentContext.Provider value={value}>
      {children}
    </UserGeneratedContentContext.Provider>
  );
}

export function useUserGeneratedContent(): UserGeneratedContentContextType {
  const context = useContext(UserGeneratedContentContext);
  if (context === undefined) {
    throw new Error(
      'useUserGeneratedContent must be used within a UserGeneratedContentProvider'
    );
  }
  return context;
}

export { UserGeneratedContentContext };
